'use client';
import Link from 'next/link';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { usePathname } from 'next/navigation';

import { Svg } from '@/components';
import { ArrowIcon } from '@/icons';
import { MENU_LINKS } from '@/constants';
import { useCleverTap } from '@/context/clevertapContext';
import { useHeaderContext } from '@/context/headerContext';
import { storage } from '@/utils/storage';

interface PropTypes {
  menuOpen?: boolean;
  onMenuClose?: () => void;
  showHoverDropdown?: boolean;
  menuData?: any;
  setMenuData?: any;
}

const NavigationLinks: FC<PropTypes> = ({
  menuOpen,
  menuData,
  setMenuData,
  onMenuClose,
}) => {
  const { pushEvent } = useCleverTap();
  const {
    giftingCategories,
    shopCategories,
    isSearchDrawerOpen,
    toggleIsSearchDrawerOpen,
    setHoverCategory,
    toggleHoverDropdown,
  } = useHeaderContext();
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isBangaloreStore, setIsBangaloreStore] = useState(false);
  useEffect(() => {
    const store = storage.get('store_code');
    if (store === 'bengaluru') {
      setIsBangaloreStore(true);
    } else {
      setIsBangaloreStore(false);
    }
  }, []);

  const pathname = usePathname().split('/')[1];
  const path = usePathname();
  const linkClass = twJoin(
    `cursor-pointer md:text-sm font-medium p-5 md:p-0`,
    menuOpen ? 'w-full' : ''
  );
  const shopButtonClass = menuOpen
    ? `${linkClass} align-text-start flex justify-between`
    : `${linkClass} pl-3`;

  const selectedLinkClass = 'text-fig underline';

  const onLinkClick = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (menuOpen) {
      if (onMenuClose) {
        onMenuClose();
      }
    }
    if (isSearchDrawerOpen) {
      toggleIsSearchDrawerOpen(false);
    }
    toggleHoverDropdown(false);
  }, [menuOpen, onMenuClose, isSearchDrawerOpen, toggleHoverDropdown]);

  const onShopGiftingClick = useCallback(
    (category: 'shop' | 'gifting') => {
      if (menuOpen) {
        setHoverCategory(category);
        setMenuData({
          prev: [],
          name:
            category === 'shop'
              ? 'Shop'
              : category === 'gifting'
                ? 'Gifting'
                : '',
          url_path:
            category === 'shop'
              ? MENU_LINKS.SHOP
              : category === 'gifting'
                ? MENU_LINKS.GIFTING
                : '',
          level: 1,
          children:
            category === 'shop'
              ? shopCategories
              : category === 'gifting'
                ? giftingCategories
                : [],
        });
      }
      if (isSearchDrawerOpen) {
        toggleIsSearchDrawerOpen(false);
      }
      toggleHoverDropdown(false);
    },
    [menuOpen, menuData, isSearchDrawerOpen, toggleHoverDropdown]
  );

  const showDropdown = (category: 'shop' | 'gifting') => {
    setHoverCategory(category);
    toggleHoverDropdown(true);
  };

  const handleMouseEnter = (category: 'shop' | 'gifting') => {
    hoverTimeoutRef.current = setTimeout(() => {
      showDropdown(category);
    }, 500);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const triggerEvent = (name: string) => {
    pushEvent({
      event: 'Navigation Link Clicked',
      data: {
        Name: name,
        Pathname: path,
      },
    });
  };

  return (
    <>
      {menuOpen ? (
        <div
          className={twMerge(
            shopButtonClass,
            pathname === 'shop' && selectedLinkClass
          )}
          onClick={() => {
            onShopGiftingClick('shop');
            triggerEvent('Shop');
          }}>
          SHOP
          <Svg
            fill='none'
            width={'1.125rem'}
            height={'1.125rem'}
            className='-rotate-90 stroke-fig'>
            <ArrowIcon />
          </Svg>
        </div>
      ) : (
        <Link
          href={MENU_LINKS.SHOP}
          prefetch={false}
          className={twMerge(
            shopButtonClass,
            pathname === 'shop' && selectedLinkClass
          )}
          onClick={() => {
            onLinkClick();
            triggerEvent('Shop');
          }}
          onMouseEnter={() => handleMouseEnter('shop')}
          onMouseLeave={handleMouseLeave}>
          SHOP
        </Link>
      )}
      {menuOpen ? (
        <Link
          href={MENU_LINKS.GIFTING}
          prefetch={false}
          className={twMerge(
            linkClass,
            pathname === 'gifting' && selectedLinkClass
          )}
          onClick={() => {
            onLinkClick();
            triggerEvent('Gifting');
          }}>
          GIFTING
        </Link>
      ) : (
        <Link
          href={MENU_LINKS.GIFTING}
          prefetch={false}
          className={twMerge(
            shopButtonClass,
            pathname === 'gifting' && selectedLinkClass
          )}
          onClick={() => {
            onLinkClick();
            triggerEvent('Gifting');
          }}
          onMouseEnter={() => toggleHoverDropdown(false)}>
          GIFTING
        </Link>
      )}
      {/* {process.env.ENABLE_RAMZAN_SPECIAL === 'true' && (
        <Link
          href={MENU_LINKS.RAMZAN_GIFTING}
          prefetch={false}
          className={twJoin(
            linkClass,
            pathname === 'ramzan-gifting' && selectedLinkClass
          )}
          onClick={() => {
            onLinkClick();
            triggerEvent('Ramzan Gifting');
          }}>
          RAMZAN GIFTING
        </Link>
      )} */}
      <Link
        href={MENU_LINKS.BULK_GIFTING}
        prefetch={false}
        className={twJoin(
          linkClass,
          pathname === 'bulk-gifting' && selectedLinkClass
        )}
        onMouseEnter={() => toggleHoverDropdown(false)}
        onClick={() => {
          onLinkClick();
          triggerEvent('Bulk Gifting');
        }}>
        BULK GIFTING
      </Link>
      <Link
        href={MENU_LINKS.STORIES}
        prefetch={false}
        className={twJoin(
          linkClass,
          pathname === 'stories' && selectedLinkClass
        )}
        onMouseEnter={() => toggleHoverDropdown(false)}
        onClick={() => {
          onLinkClick();
          triggerEvent('Stories');
        }}>
        STORIES
      </Link>
      <Link
        href='/recipes'
        prefetch={false}
        className={twJoin(
          linkClass,
          pathname === 'recipes' && selectedLinkClass
        )}
        onMouseEnter={() => toggleHoverDropdown(false)}
        onClick={() => {
          onLinkClick();
          triggerEvent('Recipes');
        }}>
        RECIPES
      </Link>
      <Link
        href='/about-us'
        prefetch={false}
        className={twJoin(
          linkClass,
          pathname === 'about-us' && selectedLinkClass
        )}
        onMouseEnter={() => toggleHoverDropdown(false)}
        onClick={() => {
          onLinkClick();
          triggerEvent('About Us');
        }}>
        ABOUT US
      </Link>
      <Link
        href='/services'
        prefetch={false}
        className={twJoin(
          linkClass,
          pathname === 'services' && selectedLinkClass
        )}
        onMouseEnter={() => toggleHoverDropdown(false)}
        onClick={() => {
          onLinkClick();
          triggerEvent('Services');
        }}>
        SERVICES
      </Link>
      {isBangaloreStore && (
        <a
          href='https://foodstories-backend-production.s3.ap-south-1.amazonaws.com/fs_/cafe-menu/GARDEN+CAFE+MENU_A3+Accordian.pdf'
          target='_blank'
          rel='noopener noreferrer'
          className={twJoin(
            linkClass,
            pathname === 'garden-cafe' && selectedLinkClass
          )}
          onMouseEnter={() => toggleHoverDropdown(false)}
          onClick={() => {
            onLinkClick();
            triggerEvent('Garden Cafe');
          }}>
          GARDEN CAFE
        </a>
      )}
    </>
  );
};

export default NavigationLinks;
